import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FaCheckCircle, FaTicketAlt, FaCalendarAlt, FaMapMarkerAlt, FaEnvelope, FaDownload, FaPrint } from 'react-icons/fa';

const CompraExitosa = () => {
  const location = useLocation();
  const { tickets, event, section, quantity, total, userName } = location.state || {};
  useEffect(() => {
    // Scroll to top cuando se carga la página
    window.scrollTo(0, 0);
  }, []);

  if (!tickets || !event) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="bg-red-500/20 border border-red-500 text-red-100 px-6 py-4 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p>No se encontraron datos de la compra.</p>
          <Link to="/eventos" className="mt-4 inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors">
            Volver a eventos
          </Link>
        </div>
      </div>
    );
  }

  const formatCLP = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const options = { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('es-CL', options);
  };

  const formatShortDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('es-CL', options);
  };

  const formatTime = (dateString) => {
    const options = { 
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleTimeString('es-CL', options);
  };

  // Función para imprimir tickets
  const handlePrintTickets = () => {
    window.print();
  };

  // Función para descargar como imagen (simplificada)
  const handleDownloadTickets = () => {
    alert('Función de descarga en desarrollo. Por ahora puedes imprimir los tickets o guardar el correo electrónico.');
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Animated background elements - MEJORADO */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-bounce" style={{animationDelay: '1000ms'}}></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-br from-green-400 to-blue-500 rounded-full animate-pulse" style={{animationDelay: '2000ms'}}></div>
        <div className="absolute bottom-20 right-1/3 w-28 h-28 bg-gradient-to-br from-red-400 to-purple-500 rounded-full animate-bounce" style={{animationDelay: '500ms'}}></div>
        {/* Elementos adicionales para más movimiento */}
        <div className="absolute top-1/3 left-1/2 w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full animate-pulse" style={{animationDelay: '1500ms'}}></div>
        <div className="absolute top-2/3 right-10 w-16 h-16 bg-gradient-to-br from-pink-400 to-red-500 rounded-full animate-bounce" style={{animationDelay: '2500ms'}}></div>
        <div className="absolute bottom-1/3 left-20 w-24 h-24 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full animate-pulse" style={{animationDelay: '3000ms'}}></div>
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        {/* Header de éxito */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-600 rounded-full mb-6 shadow-lg shadow-green-600/50 animate-pulse">
            <FaCheckCircle className="text-4xl text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-green-400 via-emerald-500 to-teal-400 bg-clip-text text-transparent animate-pulse">
            ¡Compra Realizada con Éxito!
          </h1>
          <p className="text-xl text-gray-300">
            Hola <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent font-semibold">{userName}</span>, tu compra ha sido procesada correctamente.
          </p>
          <div className="mt-6 inline-flex items-center bg-blue-900/20 border border-blue-500 rounded-lg px-6 py-3 shadow-lg shadow-blue-500/25">
            <FaEnvelope className="mr-3 text-blue-400" />
            <span className="text-blue-300">Hemos enviado los detalles a tu correo electrónico</span>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Información del evento */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-700 shadow-xl shadow-purple-500/10 hover:shadow-purple-500/20 transition-shadow duration-300">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <FaTicketAlt className="mr-3 text-yellow-400" />
                Detalles del Evento
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <img 
                    src={event.image} 
                    alt={event.title}
                    className="w-24 h-24 rounded-lg object-cover border-2 border-zinc-600 hover:border-purple-500 transition-colors duration-300"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/96?text=Sin+Imagen';
                    }}
                  />
                  <div>
                    <h3 className="text-xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">{event.title}</h3>
                    <p className="text-gray-300 font-medium">{event.comedian}</p>
                    <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium shadow-lg ${
                      event.type === 'bar' 
                        ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-500/25' 
                        : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-500/25'
                    }`}>
                      {event.type === 'bar' ? 'Bar' : 'Teatro'}
                    </span>
                  </div>
                </div>
                
                <div className="border-t border-zinc-700 pt-4 space-y-3">
                  <div className="flex items-center text-gray-300">
                    <FaCalendarAlt className="mr-3 text-blue-400 flex-shrink-0" />
                    <div>
                      <span className="font-medium">{event.date}</span>
                      <span className="mx-2">•</span>
                      <span>{event.time} hrs</span>
                    </div>
                  </div>
                  <div className="flex items-center text-gray-300">
                    <FaMapMarkerAlt className="mr-3 text-red-400 flex-shrink-0" />
                    <div>
                      <div className="font-medium">{event.venue}</div>
                      <div className="text-sm text-gray-400">{event.location}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Resumen de compra */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-700 shadow-xl shadow-green-500/10 hover:shadow-green-500/20 transition-shadow duration-300">
              <h2 className="text-2xl font-bold mb-6">Resumen de Compra</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-300">Sección:</span>
                  <span className="font-semibold">{section.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Cantidad:</span>
                  <span className="font-semibold">{quantity} entrada{quantity > 1 ? 's' : ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Precio unitario:</span>
                  <span className="font-semibold">{formatCLP(section.price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Subtotal:</span>
                  <span className="font-semibold">{formatCLP(total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Cargo por servicio (5%):</span>
                  <span className="text-gray-400">{formatCLP(Math.round(total * 0.05))}</span>
                </div>
                <div className="border-t border-zinc-700 pt-4">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total Pagado:</span>
                    <span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">{formatCLP(total + Math.round(total * 0.05))}</span>
                  </div>
                </div>
                <div className="bg-zinc-800 rounded-lg p-3 mt-4">
                  <div className="text-sm text-gray-400">Fecha de compra:</div>
                  <div className="font-medium">{new Date().toLocaleDateString('es-CL', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Tickets */}
          <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-700 mb-8 shadow-xl shadow-yellow-500/10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Tus Tickets</h2>
              <div className="flex gap-2">
                <button 
                  onClick={handlePrintTickets}
                  className="flex items-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-300 shadow-lg shadow-blue-500/25 hover:scale-105"
                >
                  <FaPrint className="mr-2" />
                  Imprimir
                </button>
                <button 
                  onClick={handleDownloadTickets}
                  className="flex items-center bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg transition-all duration-300 shadow-lg shadow-green-500/25 hover:scale-105"
                >
                  <FaDownload className="mr-2" />
                  Descargar
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tickets.map((ticket, index) => (
                <div 
                  key={ticket.ticketId || index} 
                  className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-lg p-6 border border-zinc-600 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:border-purple-500"
                  style={{
                    animationDelay: `${index * 200}ms`
                  }}
                >
                  <div className="text-center">
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-3 py-1 rounded-full text-sm font-bold mb-4 inline-block shadow-lg">
                      Ticket #{index + 1}
                    </div>
                    
                    <div className="bg-black rounded-lg p-4 mb-4 border-2 border-dashed border-zinc-600 hover:border-yellow-400 transition-colors duration-300">
                      <p className="text-xs text-gray-400 mb-2">Código del Ticket:</p>
                      <p className="text-lg font-mono font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent tracking-wider break-all">
                        {ticket.codigoTicket}
                      </p>
                    </div>
                    
                    {/* QR Code */}
                    <div className="flex justify-center mb-4">
                      <div className="bg-white p-2 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${ticket.codigoTicket}&bgcolor=ffffff&color=000000`}
                          alt="QR Code"
                          className="w-30 h-30"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Estado:</span>
                        <span className="text-green-400 font-semibold">{ticket.estadoTicket}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Precio:</span>
                        <span className="font-semibold">{formatCLP(ticket.precio)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Información importante */}
          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/50 rounded-xl p-6 mb-8 shadow-xl shadow-blue-500/20">
            <h2 className="text-xl font-bold mb-4 flex items-center text-blue-300">
              <FaEnvelope className="mr-3 text-blue-400" />
              Información Importante
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
              <div className="space-y-3">
                <p className="flex items-start">
                  <span className="text-green-400 mr-2 mt-1">✓</span>
                  Hemos enviado los detalles de tu compra y los códigos QR a tu correo electrónico.
                </p>
                <p className="flex items-start">
                  <span className="text-green-400 mr-2 mt-1">✓</span>
                  Presenta cualquiera de los códigos QR o el código alfanumérico en la entrada del evento.
                </p>
              </div>
              <div className="space-y-3">
                <p className="flex items-start">
                  <span className="text-green-400 mr-2 mt-1">✓</span>
                  Te recomendamos llegar 30 minutos antes del inicio del show.
                </p>
                <p className="flex items-start">
                  <span className="text-green-400 mr-2 mt-1">✓</span>
                  Conserva este comprobante y el correo electrónico como respaldo.
                </p>
              </div>
            </div>
          </div>

          {/* Recomendaciones adicionales */}
          {event.type === 'bar' ? (
            <div className="bg-gradient-to-r from-orange-900/20 to-red-900/20 border border-orange-500/50 rounded-xl p-6 mb-8 shadow-xl shadow-orange-500/20">
              <h3 className="text-lg font-bold mb-3 bg-gradient-to-r from-orange-300 to-red-400 bg-clip-text text-transparent">Recomendaciones para el Bar</h3>
              <ul className="space-y-2 text-gray-300">
                <li>• El pago de consumos se realiza por separado en el bar</li>
                <li>• Llegada recomendada: 30 minutos antes del show</li>
                <li>• Ambiente informal, puedes ir como te sientas cómodo</li>
                <li>• Se permite el consumo durante el espectáculo</li>
              </ul>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 border border-purple-500/50 rounded-xl p-6 mb-8 shadow-xl shadow-purple-500/20">
              <h3 className="text-lg font-bold mb-3 bg-gradient-to-r from-purple-300 to-indigo-400 bg-clip-text text-transparent">Recomendaciones para el Teatro</h3>
              <ul className="space-y-2 text-gray-300">
                <li>• Llegada recomendada: 45 minutos antes del inicio</li>
                <li>• Vestimenta: Casual elegante recomendada</li>
                <li>• No está permitido el ingreso con alimentos o bebidas</li>
                <li>• Se solicita silenciar dispositivos móviles durante la función</li>
              </ul>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/eventos"
              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 text-center flex items-center justify-center shadow-lg shadow-red-500/25 hover:scale-105"
            >
              <FaTicketAlt className="mr-2" />
              Ver Más Eventos
            </Link>
            <Link
              to="/mis-tickets"
              className="bg-gradient-to-r from-zinc-600 to-zinc-700 hover:from-zinc-700 hover:to-zinc-800 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 text-center flex items-center justify-center shadow-lg shadow-zinc-600/25 hover:scale-105"
            >
              <FaCheckCircle className="mr-2" />
              Ver Mis Tickets
            </Link>
            <Link
              to="/perfil"
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 text-center flex items-center justify-center shadow-lg shadow-blue-500/25 hover:scale-105"
            >
              <FaEnvelope className="mr-2" />
              Mi Perfil
            </Link>
          </div>

          {/* Footer de soporte */}
          <div className="mt-12 text-center text-gray-400">
            <p className="mb-2">¿Tienes algún problema con tu compra?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link 
                to="/contacto" 
                className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent hover:from-blue-300 hover:to-purple-300 transition-all duration-300 font-semibold"
              >
                Contactar Soporte
              </Link>
              <span className="hidden sm:inline">•</span>
              <a 
                href="mailto:soporte@stupidcomedy.cl" 
                className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent hover:from-blue-300 hover:to-purple-300 transition-all duration-300"
              >
                soporte@stupidcomedy.cl
              </a>
              <span className="hidden sm:inline">•</span>
              <a 
                href="tel:+56912345678" 
                className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent hover:from-blue-300 hover:to-purple-300 transition-all duration-300"
              >
                +56 9 1234 5678
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos para impresión */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
            color: black !important;
          }
          .bg-black {
            background: white !important;
          }
          .text-white {
            color: black !important;
          }
          .bg-zinc-900 {
            background: #f5f5f5 !important;
            border: 1px solid #ddd !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CompraExitosa;